import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, shareReplay } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { DatePickerRange } from '../date-picker/date-picker.component';
import { PlanningMode } from './planning.types';

/** Sous-période dont toutes les cases partagent le même décalage UTC du navigateur. */
interface OffsetSegment {
  from: string;
  to: string;
  offsetMinutes: number;
}

/**
 * Périodes d'un site contenant du forage ou des livraisons, pour les points du
 * sélecteur de date du planning. Une requête par sous-onglet : chacun ne charge
 * que ce qu'il affiche.
 */
@Injectable({ providedIn: 'root' })
export class PlanningActivityService {
  private readonly http = inject(HttpService);
  private readonly cache = new Map<string, { expiresAt: number; stream: Observable<string[]> }>();
  /** Assez court pour qu'une livraison du jour apparaisse vite, assez long pour feuilleter sans recharger. */
  private readonly ttlMs = 5 * 60 * 1000;

  /** Jours (`YYYY-MM-DD`), mois (`YYYY-MM`) ou années (`YYYY`) actifs de la période. */
  getActivePeriods(siteId: number, mode: PlanningMode, range: DatePickerRange): Observable<string[]> {
    const key = `${siteId}_${mode}_${range.granularity}_${range.from}_${range.to}`;
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) { return cached.stream; }

    const uri = mode === 'drilling' ? `DrillSessions/site/${siteId}/activity` : `deliveries/${siteId}/activity`;
    // Le décalage de repli sert aux sites sans fuseau : la grille place alors chaque activité
    // à l'heure du navigateur en vigueur à sa date. Une période qui traverse un changement
    // d'heure est donc découpée en une requête par décalage.
    const requests = PlanningActivityService.splitByBrowserOffset(range).map(segment =>
      this.http.getWithParams<string[]>(uri, {
        from: segment.from,
        to: segment.to,
        granularity: range.granularity,
        fallbackOffsetMinutes: segment.offsetMinutes,
      }),
    );
    const stream = forkJoin(requests).pipe(
      map(results => results.flat()),
      catchError(() => {
        this.cache.delete(key);
        return of([] as string[]);
      }),
      shareReplay(1),
    );

    this.cache.set(key, { expiresAt: Date.now() + this.ttlMs, stream });
    return stream;
  }

  /**
   * Regroupe les cases consécutives de la période (jours, mois ou années) qui commencent
   * avec le même décalage UTC du navigateur.
   */
  private static splitByBrowserOffset(range: DatePickerRange): OffsetSegment[] {
    const [toYear, toMonth, toDay] = range.to.split('-').map(Number);
    const end = new Date(toYear, toMonth - 1, toDay);
    const [fromYear, fromMonth, fromDay] = range.from.split('-').map(Number);
    let current = range.granularity === 'year'
      ? new Date(fromYear, 0, 1)
      : range.granularity === 'month'
        ? new Date(fromYear, fromMonth - 1, 1)
        : new Date(fromYear, fromMonth - 1, fromDay);

    const segments: OffsetSegment[] = [];
    while (current <= end) {
      const offsetMinutes = -current.getTimezoneOffset();
      const last = segments[segments.length - 1];
      if (last && last.offsetMinutes === offsetMinutes) {
        last.to = PlanningActivityService.toIso(current);
      } else {
        const iso = PlanningActivityService.toIso(current);
        segments.push({ from: iso, to: iso, offsetMinutes });
      }
      current = range.granularity === 'year'
        ? new Date(current.getFullYear() + 1, 0, 1)
        : range.granularity === 'month'
          ? new Date(current.getFullYear(), current.getMonth() + 1, 1)
          : new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1);
    }

    // Les bornes extrêmes restent celles demandées ; en mois ou en année, l'API étend
    // d'elle-même chaque borne à la période entière qui la contient.
    if (segments.length > 0) {
      segments[0].from = range.from;
      segments[segments.length - 1].to = range.to;
    } else {
      segments.push({ from: range.from, to: range.to, offsetMinutes: -new Date().getTimezoneOffset() });
    }
    return segments;
  }

  private static toIso(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
